import type {
  TaxCodeFilterOptionsQueryParams,
  TaxCodeFilterOptionsResponse,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { VestigasHTTPError } from '@vestigas/data';

import { queryKeysTaxCode } from '../queryKeys';

import { fetchTaxCodeFilterOptions } from './fetchTaxCodeFilterOptions';
import { zodTaxCodeFilterOptionsQueryParamsSchema } from './validations';

/**
 * Generates reusable options for the tax-code filter-options query.
 *
 * @param queryParams - Company filters that scope the available values.
 * @param options - Additional query options.
 * @returns Canonical query options for fetching filter values.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
// fallow-ignore-next-line unused-export -- Query options are the intended loader and prefetch API.
export const getTaxCodeFilterOptionsQueryOptions = <
  TData = TaxCodeFilterOptionsResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<TaxCodeFilterOptionsQueryParams>;
  options?: Omit<
    UseQueryOptions<TaxCodeFilterOptionsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const canonicalQueryParams =
    zodTaxCodeFilterOptionsQueryParamsSchema.parse(queryParams);

  return queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchTaxCodeFilterOptions(canonicalQueryParams, { signal }),
    queryKey: queryKeysTaxCode.filterOptions(canonicalQueryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });
};

/**
 * Fetches tax-code names, rates, and codes available to list filters.
 *
 * @param queryParams - Company filters that scope the available values.
 * @param options - Additional query options.
 * @returns The tax-code filter-options query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const useQueryTaxCodeFilterOptions = <
  TData = TaxCodeFilterOptionsResponse,
>(
  queryParams: Partial<TaxCodeFilterOptionsQueryParams>,
  options?: Omit<
    UseQueryOptions<TaxCodeFilterOptionsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getTaxCodeFilterOptionsQueryOptions<TData>({ options, queryParams }),
  );
